/**
 * Chart UI Components (Controls, Legend)
 */
import { ChartControls } from "../ChartControls";
import { ChartLegend } from "../ChartLegend";
import { ChartTheme } from "../../theme";
import { ChartOptions } from "../../types";
import { Series } from "../Series";
import type { InteractionMode } from "../InteractionManager";

export interface UIContext {
  container: HTMLDivElement;
  theme: ChartTheme;
  showControls: boolean;
  toolbar?: import("../../types").ToolbarOptions;
  showLegend: boolean;
  /** Legend behavior options */
  legendOptions?: {
    highlightOnHover?: boolean;
    bringToFrontOnHover?: boolean;
  };
  series: Map<string, Series>;
  autoScale: () => void;
  resetZoom: () => void;
  requestRender: () => void;
  exportImage: () => string;
  setPanMode: (active: boolean) => void;
  setMode: (mode: InteractionMode) => void;
  onLegendMove: (x: number, y: number) => void;
  onToggleSmoothing: () => void;
  toggleLegend: () => void;
  onInteractionStart?: () => void;
  onInteractionEnd?: () => void;
  onHoverStart?: () => void;
  onHoverEnd?: () => void;
  /** @param highlightColor - Whether to apply color highlighting */
  onSeriesHoverStart?: (series: Series, highlightColor: boolean) => void;
  /** @param highlightColor - Whether color highlighting was applied */
  onSeriesHoverEnd?: (series: Series, highlightColor: boolean) => void;
  onToggleVisibility?: (series: Series) => void;
}

export function initControls(ctx: UIContext): ChartControls | null {
  if (!ctx.showControls) return null;
  return new ChartControls(ctx.container, ctx.theme, {
    onResetZoom: () => ctx.resetZoom(),
    onSetType: (type) => {
      ctx.series.forEach((s) => {
        const currentType = s.getType();
        const style = s.getStyle();
        const isStepCapable = currentType === "step" || currentType === "step+scatter" || style.stepMode !== undefined;
        if (isStepCapable) {
          const mappedType = type === "line" ? "step" : type === "line+scatter" ? "step+scatter" : "scatter";
          s.setType(mappedType as any);
        } else if (currentType === "line" || currentType === "scatter" || currentType === "line+scatter") {
          s.setType(type as any);
        }
      });
      ctx.requestRender();
    },
    onToggleSmoothing: () => {
      ctx.series.forEach((s) => {
        const style = s.getStyle();
        s.setStyle({ smoothing: (style.smoothing || 0) === 0 ? 0.5 : 0 });
      });
      ctx.requestRender();
    },
    onTogglePan: (active) => ctx.setPanMode(active),
    onSetMode: (mode) => ctx.setMode(mode),
    onExport: () => {
      const link = document.createElement("a");
      link.download = `velo-plot-export-${Date.now()}.png`;
      link.href = ctx.exportImage();
      link.click();
    },
    onAutoScale: () => {
      ctx.autoScale();
      ctx.requestRender();
    },
    onToggleLegend: () => {
      ctx.toggleLegend();
    },
    onHoverStart: () => ctx.onHoverStart?.(),
    onHoverEnd: () => ctx.onHoverEnd?.(),
  }, ctx.toolbar);
}

export function initLegend(ctx: UIContext, options: ChartOptions): ChartLegend | null {
  if (!ctx.showLegend) return null;

  // Merge legend options from layout config and legacy legendPosition
  const legendOpts = {
    ...options.legendPosition,
    highlightOnHover: ctx.legendOptions?.highlightOnHover ?? options.layout?.legend?.highlightOnHover ?? false,
    bringToFrontOnHover: ctx.legendOptions?.bringToFrontOnHover ?? options.layout?.legend?.bringToFrontOnHover ?? true,
  };

  const legend = new ChartLegend(
    ctx.container,
    ctx.theme,
    legendOpts,
    {
      onMove: (x, y) => ctx.onLegendMove(x, y),
      onInteractionStart: () => ctx.onInteractionStart?.(),
      onInteractionEnd: () => ctx.onInteractionEnd?.(),
      onHoverStart: () => ctx.onHoverStart?.(),
      onHoverEnd: () => ctx.onHoverEnd?.(),
      onSeriesHoverStart: (s, highlightColor) => ctx.onSeriesHoverStart?.(s, highlightColor),
      onSeriesHoverEnd: (s, highlightColor) => ctx.onSeriesHoverEnd?.(s, highlightColor),
      onToggleVisibility: (s) => ctx.onToggleVisibility?.(s),
    }
  );
  legend.update(Array.from(ctx.series.values()));
  return legend;
}
